import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  clearViewHistory as clearRemoteHistory,
  getViewHistoryIds,
  mergeViewHistory,
  recordProductView,
} from "@/lib/account/history.functions";

const KEY = "fixhen.recentlyViewed";
const MERGED_FLAG = "fixhen.recentlyViewed.mergedFor";
const MAX = 24;

function readIds(): string[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed)
      ? parsed.filter((x) => typeof x === "string").slice(0, MAX)
      : [];
  } catch {
    return [];
  }
}

function writeIds(ids: string[]) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(KEY, JSON.stringify(ids.slice(0, MAX)));
    window.dispatchEvent(new CustomEvent("fixhen:recently-viewed-change"));
  } catch {}
}

function isUuid(id: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id);
}

/** Read the current recently-viewed list. SSR-safe: returns [] until mounted.
 *  When a session is present, merges the server-side history (across devices)
 *  with the local list, remote entries taking priority in ordering. */
export function useRecentlyViewedIds(exclude?: string) {
  const [ids, setIds] = useState<string[]>([]);

  useEffect(() => {
    let cancelled = false;
    setIds(readIds());

    const onLocalChange = () => setIds(readIds());
    window.addEventListener("fixhen:recently-viewed-change", onLocalChange);
    window.addEventListener("storage", onLocalChange);

    async function syncFromServer() {
      const { data } = await supabase.auth.getSession();
      const session = data.session;
      if (!session) return;

      const local = readIds();
      const mergedFor = window.localStorage.getItem(MERGED_FLAG);
      if (mergedFor !== session.user.id && local.length) {
        try {
          await mergeViewHistory({ data: { localIds: local.filter(isUuid) } });
          window.localStorage.setItem(MERGED_FLAG, session.user.id);
        } catch (e) {
          console.warn("mergeViewHistory failed", e);
        }
      }

      try {
        const remote = await getViewHistoryIds();
        if (cancelled) return;
        // Remote wins on ordering; append any local ids not present
        const seen = new Set(remote);
        const merged = [...remote, ...local.filter((id) => !seen.has(id))].slice(0, MAX);
        writeIds(merged);
      } catch (e) {
        console.warn("getViewHistoryIds failed", e);
      }
    }

    syncFromServer();

    const { data: sub } = supabase.auth.onAuthStateChange((event) => {
      if (event === "SIGNED_IN") syncFromServer();
      if (event === "SIGNED_OUT") {
        window.localStorage.removeItem(MERGED_FLAG);
      }
    });

    return () => {
      cancelled = true;
      window.removeEventListener("fixhen:recently-viewed-change", onLocalChange);
      window.removeEventListener("storage", onLocalChange);
      sub.subscription.unsubscribe();
    };
  }, []);

  return exclude ? ids.filter((id) => id !== exclude) : ids;
}

/** Mark a product as viewed after a small delay so bounces don't count.
 *  Idempotent: calling with the same id twice in a session moves it to the front.
 *  When signed in, also persists to the server (fire-and-forget). */
export function useTrackRecentlyViewed(productId: string | undefined) {
  const recordedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!productId) return;
    const t = window.setTimeout(() => {
      const cur = readIds();
      const next = [productId, ...cur.filter((id) => id !== productId)].slice(0, MAX);
      writeIds(next);

      if (isUuid(productId) && recordedRef.current !== productId) {
        recordedRef.current = productId;
        supabase.auth.getSession().then(({ data }) => {
          if (!data.session) return;
          recordProductView({ data: { productId } }).catch(() => {});
        });
      }
    }, 3000);
    return () => window.clearTimeout(t);
  }, [productId]);
}

export function useClearRecentlyViewed() {
  return useCallback(async () => {
    writeIds([]);
    const { data } = await supabase.auth.getSession();
    if (data.session) {
      await clearRemoteHistory().catch(() => {});
    }
  }, []);
}
